import type { TextDocument } from "vscode";

import type { IHistoryEntry } from "../editor-history";

import { numCodeUnitsToNumUtf8Bytes } from "./utf";

export const OPT_OUT_LENGTH = 50000;

export interface TextAndOffsets {
  // The smart concatenation of all notebook cells, or just the text of the main document.
  text: string;
  // The offset into the current cell/document.
  utf8ByteOffset: number;
  // Any additional offset induced by the smart concatenation.
  additionalUtf8ByteOffset: number;
}

export interface ComputeTextOpts {
  readonly history: IHistoryEntry[];
  readonly currentDocument: TextDocument;
  // The offset into the value of getText(currentTextModel) at which to trigger a completion.
  readonly utf16CodeUnitOffset: number;
  getText(doc: TextDocument): string;
  getOpenModel(uri: string): TextDocument | null | undefined;
}

function getLastRelevantFiles(
  history: IHistoryEntry[],
  currentDocument: TextDocument,
): string[] {
  const deduplicationSet = new Set<string>();
  const relevantFiles: string[] = [];
  for (let i = history.length - 1; i >= 0; i--) {
    const entry = history[i];
    if (entry.model.type !== "code") {
      continue;
    }

    const entryUri = entry.model.uri.toString();
    if (entryUri === currentDocument.uri.toString()) {
      continue;
    }

    if (deduplicationSet.has(entryUri)) {
      continue;
    }

    if (entry.model.language === currentDocument.languageId) {
      deduplicationSet.add(entryUri);
      relevantFiles.push(entryUri);
    }
  }
  return relevantFiles;
}

export function computeTextAndOffsets(opts: ComputeTextOpts): TextAndOffsets {
  const {
    history,
    currentDocument,
    utf16CodeUnitOffset,
    getOpenModel,
    getText,
  } = opts;
  const relevantFiles = getLastRelevantFiles(history, currentDocument);
  const currentModelText = getText(currentDocument);
  const additionalText = relevantFiles
    .map((uri) => {
      const textDocument = getOpenModel(uri);
      return textDocument ? getText(textDocument) : "";
    })
    .filter((t) => t && t.length < OPT_OUT_LENGTH)
    .slice(0, 3)
    .join("\n\n");
  let additionalUtf8ByteOffset = numCodeUnitsToNumUtf8Bytes(additionalText);
  let text = "";
  if (additionalText.length > 0) {
    text += additionalText;
    text += "\n\n";
    additionalUtf8ByteOffset += 2;
  }
  text += currentModelText;
  const utf8ByteOffset = numCodeUnitsToNumUtf8Bytes(
    currentModelText,
    utf16CodeUnitOffset,
  );
  return {
    text,
    utf8ByteOffset,
    additionalUtf8ByteOffset,
  };
}
